<script setup lang="ts">
// Server-side paging footer. The rows are paged in SQL, so this is the whole
// client-side paging surface: page N of M plus first/prev/next/last.
import { computed } from "vue";

const props = withDefaults(
	defineProps<{
		page: number;
		limit: number;
		total: number;
		loading?: boolean;
	}>(),
	{ loading: false },
);

const emit = defineEmits<{ (e: "page", page: number): void }>();

const pageCount = computed(() =>
	Math.max(1, Math.ceil(props.total / Math.max(1, props.limit))),
);

const first = computed(() =>
	props.total === 0 ? 0 : props.page * props.limit + 1,
);
const last = computed(() =>
	Math.min(props.total, (props.page + 1) * props.limit),
);

function go(page: number) {
	const next = Math.min(pageCount.value - 1, Math.max(0, page));
	if (next !== props.page) emit("page", next);
}
</script>

<template>
	<div class="table-pager">
		<span class="table-pager-range">
			<template v-if="total === 0">No rows</template>
			<template v-else
				>{{ first.toLocaleString() }}–{{ last.toLocaleString() }} of
				{{ total.toLocaleString() }}</template
			>
		</span>
		<div class="table-pager-buttons">
			<button
				type="button"
				:disabled="loading || page <= 0"
				aria-label="First page"
				title="First page"
				@click="go(0)"
			>
				<i class="bi bi-chevron-double-left"></i>
			</button>
			<button
				type="button"
				:disabled="loading || page <= 0"
				aria-label="Previous page"
				title="Previous page"
				@click="go(page - 1)"
			>
				<i class="bi bi-chevron-left"></i>
			</button>
			<span class="table-pager-page">{{ page + 1 }} / {{ pageCount }}</span>
			<button
				type="button"
				:disabled="loading || page >= pageCount - 1"
				aria-label="Next page"
				title="Next page"
				@click="go(page + 1)"
			>
				<i class="bi bi-chevron-right"></i>
			</button>
			<button
				type="button"
				:disabled="loading || page >= pageCount - 1"
				aria-label="Last page"
				title="Last page"
				@click="go(pageCount - 1)"
			>
				<i class="bi bi-chevron-double-right"></i>
			</button>
		</div>
	</div>
</template>

<style scoped>
.table-pager {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 2px 10px;
	border-top: 1px solid #dee2e6;
	background: #f8f9fa;
	font-size: 0.75rem;
	color: #495057;
	flex: 0 0 auto;
}

.table-pager-buttons {
	display: flex;
	align-items: center;
	gap: 2px;
}

.table-pager-buttons button {
	border: 0;
	background: transparent;
	color: #495057;
	padding: 1px 6px;
	line-height: 1.2;
	font-size: 0.75rem;
}

.table-pager-buttons button:disabled {
	color: #ced4da;
}

.table-pager-page {
	padding: 0 6px;
	min-width: 64px;
	text-align: center;
}
</style>
